import React, { useMemo } from "react";
import { daysLeft, parseApiDate } from "@/helpers/daysLeft";
import Modal from "react-bootstrap/Modal";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import AgentBioHeader from "@/components/AgentBioHeader";
import AgentBioSections from "@/components/AgentBioSections";
import { BioLegalLinks } from "@/components/BioDisclosure";
import ScaledPreview from "@/components/ScaledPreview";
import { draftFrom, previewAgent } from "@/components/BioStudio/bioStudioModel";
import { useAgentPublicProfile } from "@/hooks/useAgentPublicProfile";
import { useGitchaProOffer } from "@/hooks/useGitchaPro";
import useGitchaProCheckout from "@/hooks/useGitchaProCheckout";
import useIsMobile from "@/hooks/useIsMobile";
import { getCardIcon } from "@/helpers/common";
import GitchaLogo from "@/images/gitcha-logo.svg";
import "./GitchaProWindow.scss";


const BENEFITS = [
  {
    title: "Buyers and sellers reach you directly",
    body: "A short form on your bio sends their name, timing and what they need straight to you.",
  },
  {
    title: "Your best work on one page",
    body: "Your photo, logo, story, client testimonials and links, in the order you choose.",
  },
];

const REASONS = {
  buyerListingForm:
    "Gitcha Pro adds your Buyer Listing Form: send your buyer a link, they fill in what they want, and you review and publish the Buyer Want-Listing.",
};

export const formatPrice = (amount) => {
  if (amount == null || Number.isNaN(Number(amount))) return null;
  const value = Number(amount);
  return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;
};

export const formatDay = (value) =>
  value
    ? parseApiDate(value).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      })
    : null;

export { daysLeft, parseApiDate };

export const windowStateOf = (offer) => {
  if (!offer) return "loading";
  if (offer.isPro) return "pro";
  if (offer.onTrial) return offer.keepingPro ? "kept" : "trial";
  if (offer.canStartTrial) return "offer";
  if (offer.canUpgrade) return "upgrade";
  return "unavailable";
};

export const CardPicker = ({ card, onChange, onAdd }) =>
  card ? (
    <button type="button" className="gitcha-pro__card" onClick={onChange}>
      <img src={getCardIcon(card.Brand)} alt="" />
      <span className="gitcha-pro__card-text">
        <strong>
          {card.Brand} ending {card.Last4}
        </strong>
        <span>
          Expires {String(card.Exp_Month).padStart(2, "0")}/
          {String(card.Exp_Year).slice(-2)}
        </span>
      </span>
      <span className="gitcha-pro__card-action">Change</span>
    </button>
  ) : (
    <button type="button" className="gitcha-pro__card is-empty" onClick={onAdd}>
      <span className="gitcha-pro__card-text">
        <strong>Add a card</strong>
        <span>Visa, Mastercard, American Express or Discover</span>
      </span>
      <span className="gitcha-pro__card-action">Add</span>
    </button>
  );

const GitchaProWindow = ({
  show,
  onHide,
  reason,
  reasonText,
  userDetails,
  onChanged,
  onCancelPlan,
}) => {
  const isMobile = useIsMobile();
  const storedDetails = useSelector((state) => state.userProfile.userDetails);
  const details = userDetails?.slug ? userDetails : storedDetails?.[0] || userDetails;
  const offerQuery = useGitchaProOffer(Boolean(show));
  const offer = offerQuery.data;
  const state = offerQuery.isError ? "error" : windowStateOf(offer);
  const profile = useAgentPublicProfile(show ? details?.slug : null).data;
  const agent = useMemo(
    () => (details ? previewAgent(details, draftFrom(details), true, profile) : null),
    [details, profile],
  );

  const finish = (message) => {
    toast.dismiss();
    toast.success(message);
    onChanged && onChanged();
    onHide();
  };

  const checkout = useGitchaProCheckout({
    show,
    onTrialStarted: () => finish("Your 30 days of Gitcha Pro have started."),
    onKept: (next) => finish(`Gitcha Pro continues on ${formatDay(next?.trialEndsOn)}.`),
    onUpgraded: () => finish("You have Gitcha Pro."),
  });
  const { billing, setBilling, card, error, setError, startTrial, keepPro, upgradeNow, upgrading, busy } = checkout;

  const price = formatPrice(offer?.price?.amount);
  const trialEnd = formatDay(offer?.trialEndsOn);
  const left = daysLeft(offer?.trialEndsOn);
  const networkName = offer?.networkName || "your MLS";

  const heading = {
    loading: "Gitcha Pro",
    error: "Gitcha Pro",
    offer: "Let buyers and sellers reach you from your Gitcha bio",
    trial: `${left} ${left === 1 ? "day" : "days"} left in your Gitcha Pro trial`,
    kept: `Gitcha Pro continues on ${trialEnd}`,
    upgrade: "Bring back Gitcha Pro on your bio",
    pro: "You have Gitcha Pro",
    unavailable: "Gitcha Pro",
  }[state];

  const lede = {
    offer: `Gitcha Pro is optional. Everything in your ${networkName} Buyer Listing Service stays included either way.`,
    trial: `Your bio has every Pro feature until ${trialEnd}. Keep Pro and nothing changes for buyers looking at your page.`,
    kept: `Your bio keeps every Pro feature. Billing starts ${trialEnd} at ${price} a month, and you can cancel before then.`,
    upgrade: `Your bio is on the free plan. Pro brings back the request form, testimonials and links for ${price} a month.`,
    pro: "Your bio has the request form, testimonials, links and more.",
    unavailable: "Gitcha Pro is available to agents who sign in through their MLS Buyer Listing Service.",
    error: "Gitcha Pro could not load right now. Please try again in a moment.",
  }[state];

  const billingLine =
    state === "trial"
      ? `Nothing is billed today. ${price} a month starting ${trialEnd}. Cancel anytime.`
      : `${price} billed today, then every month. Cancel anytime.`;

  const planRows = [
    ["Plan", details?.PlanName],
    [
      "Price",
      details?.Price != null && Number(details.Price) > 0
        ? `${formatPrice(details.Price)} a ${details?.period === 3 ? "year" : "month"}`
        : null,
    ],
    ["Renews", formatDay(details?.ToDate)],
  ].filter(([, value]) => Boolean(value));

  const showPreview = agent && ["offer", "trial", "upgrade"].includes(state);

  return (
    <>
      <Modal
        show={show && !checkout.covered}
        onHide={onHide}
        centered
        size={showPreview && !billing ? "xl" : "md"}
        fullscreen={isMobile}
        className="gitcha-pro-modal"
        aria-labelledby="gitcha-pro-title"
      >
        <Modal.Body className="gitcha-pro">
          <button type="button" className="btn-close" aria-label="Close" onClick={onHide} />
          <div className="gitcha-pro__brand">
            <img src={GitchaLogo} alt="Gitcha" />
          </div>

          {state === "loading" ? (
            <div className="gitcha-pro__skeleton" aria-busy="true">
              <span />
              <span />
              <span />
            </div>
          ) : (
            <div className={`gitcha-pro__layout${showPreview && !billing ? " has-preview" : ""}`}>
              <div className="gitcha-pro__main">
                {(reasonText || (reason && REASONS[reason])) && state !== "pro" ? (
                  <p className="gitcha-pro__reason">{reasonText || REASONS[reason]}</p>
                ) : null}
                <h2 id="gitcha-pro-title" className="gitcha-pro__title">
                  {billing && state === "trial" ? "Keep Gitcha Pro" : heading}
                </h2>
                {!billing ? <p className="gitcha-pro__lede">{lede}</p> : null}

                {state === "pro" && planRows.length ? (
                  <dl className="gitcha-pro__plan">
                    {planRows.map(([label, value]) => (
                      <div key={label}>
                        <dt>{label}</dt>
                        <dd>{value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : null}

                {!billing && ["offer", "trial", "upgrade", "pro"].includes(state) ? (
                  <ul className="gitcha-pro__benefits">
                    {BENEFITS.map((benefit) => (
                      <li key={benefit.title}>
                        <strong>{benefit.title}</strong>
                        <span>{benefit.body}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}

                {state === "offer" ? (
                  <div className="gitcha-pro__price">
                    <strong>Free for {offer.trialDays} days, then {price} a&nbsp;month</strong>
                    <span>
                      No card needed to start. When the trial ends your bio goes back to free unless you choose to keep&nbsp;Pro.
                    </span>
                  </div>
                ) : null}

                {billing ? (
                  <div className="gitcha-pro__billing">
                    <p className="gitcha-pro__billing-line">{billingLine}</p>
                    <CardPicker
                      card={card}
                      onChange={checkout.changeCard}
                      onAdd={checkout.addCard}
                    />
                  </div>
                ) : null}

                {error ? (
                  <p className="gitcha-pro__error" role="alert">
                    {error}
                  </p>
                ) : null}

                <div className="gitcha-pro__actions">
                  {state === "offer" ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary gitcha-pro__cta"
                        disabled={busy}
                        onClick={() => {
                          setError(null);
                          startTrial.mutate();
                        }}
                      >
                        {startTrial.isPending ? "Starting your trial" : `Start ${offer.trialDays}-day free trial`}
                      </button>
                      <button type="button" className="gitcha-pro__quiet" onClick={onHide}>
                        Maybe later
                      </button>
                    </>
                  ) : null}

                  {(state === "trial" || state === "upgrade") && !billing ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary gitcha-pro__cta"
                        onClick={() => {
                          setError(null);
                          setBilling(true);
                        }}
                      >
                        {state === "trial" ? "Keep Gitcha Pro" : `Upgrade for ${price} a month`}
                      </button>
                      <button type="button" className="gitcha-pro__quiet" onClick={onHide}>
                        {state === "trial" ? "Not now" : "Maybe later"}
                      </button>
                    </>
                  ) : null}

                  {billing ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary gitcha-pro__cta"
                        disabled={!card || busy}
                        onClick={() => {
                          setError(null);
                          if (state === "trial") keepPro.mutate(card.Payment_Mode_Id);
                          else upgradeNow();
                        }}
                      >
                        {state === "trial"
                          ? keepPro.isPending
                            ? "Saving"
                            : `Keep Pro for ${price} a month`
                          : upgrading
                            ? "Upgrading"
                            : `Pay ${price} and upgrade`}
                      </button>
                      <button
                        type="button"
                        className="gitcha-pro__quiet"
                        disabled={busy}
                        onClick={() => {
                          setError(null);
                          setBilling(false);
                        }}
                      >
                        Back
                      </button>
                    </>
                  ) : null}

                  {state === "kept" || state === "pro" ? (
                    <>
                      <button type="button" className="btn btn-primary gitcha-pro__cta" onClick={onHide}>
                        Done
                      </button>
                      {onCancelPlan ? (
                        <button type="button" className="gitcha-pro__quiet" onClick={onCancelPlan}>
                          Cancel plan
                        </button>
                      ) : null}
                    </>
                  ) : null}

                  {state === "unavailable" || state === "error" ? (
                    <button type="button" className="btn btn-primary gitcha-pro__cta" onClick={onHide}>
                      Close
                    </button>
                  ) : null}
                </div>
              </div>

              {showPreview && !billing ? (
                <aside className="gitcha-pro__preview">
                  <span className="gitcha-pro__preview-label">Your bio with Pro</span>
                  <ScaledPreview width={880} maxHeight={isMobile ? 280 : 560}>
                    <AgentBioHeader agent={agent} isGitchaWebsite proBadge onDirectMessage={() => {}} />
                    <AgentBioSections agent={agent} onOpen={() => {}} />
                    <BioLegalLinks agent={agent} />
                  </ScaledPreview>
                </aside>
              ) : null}
            </div>
          )}
        </Modal.Body>
      </Modal>
      {checkout.modals}
    </>
  );
};

export default GitchaProWindow;
